/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */

import { Box, CircularProgress } from '@material-ui/core';
import { RouterUrlEnum } from 'enums/routerUrl';
import { StorageKeyEnum } from 'enums/StorageKeyEnum';
import React, { Component, ComponentType, LazyExoticComponent } from 'react';
import { connect } from 'react-redux';
import { Redirect, Route, RouteComponentProps, RouteProps } from 'react-router-dom';
import { AppState } from 'store';
import { getChosenProjectSelector, getIsDirtySelector, getIsLoggedInSelector } from 'store/auth/auth.selectors';
import { getItem } from 'utils/handlers/storageHandler';
import { isInIFrame } from 'utils/services/kenticoSdk.service';

interface IProps {
  restricted?: boolean;
  privateRoute: boolean;
  iframe?: boolean;
  globalConfig?: any;
  detailedConfig?: any;
  component: ComponentType<any> | ComponentType<RouteComponentProps<any>> | LazyExoticComponent<ComponentType<any>>;
}

interface IStateProps {
  isLoggedIn: boolean;
  chosenProject: boolean;
  isDirty: boolean;
}

export type RoutePropsType = IProps & RouteProps & IStateProps;

/**
 * @component
 * Component for rendering custom Route component with guard redirecting user to adequately route based on user login state and privateRoute prop.
 *
 * If user is logged public route with restricted prop can't be accessed.
 *
 * If user is not logged private routes can't be accesed.
 *
 * @visibleName
 * Extended Route Component
 *
 * @example
 * const props:RoutePropsType = {...};
 *
 * return (<ExtendedRoute {...props} />);
 *
 * return (
 *  <ExtendedRoute
 *   path={pathname}
 *   exact={!!exact}
 *   privateRoute={privateRoute}
 *   restricted={!!restricted}
 *   component={component}
 *   globalConfig={globalConfig}
 *   detailedConfig={configElement?.config}
 *   key={!!pathname.length ? (pathname as string) : pathname[0]}
 *  />
 * );
 */

class ExtendedRoute extends Component<RoutePropsType> {
  getComponent(): JSX.Element {
    const {
      component: PropertyComponent,
      isLoggedIn,
      restricted,
      detailedConfig,
      globalConfig,
      chosenProject,
      privateRoute,
      isDirty,
      iframe,
    } = this.props;

    if (!isDirty && getItem(StorageKeyEnum.refreshTokenStorageKey)) {
      return (
        <Box height="100%" width="100%" display="flex" justifyContent="center" alignItems="center">
          <CircularProgress color="secondary" />
        </Box>
      );
    }
    if (!!privateRoute) {
      if (!isLoggedIn) {
        return <Redirect to={RouterUrlEnum.LOGIN} />;
      }

      if ((iframe && !isInIFrame) || (chosenProject && restricted)) {
        return <Redirect to={RouterUrlEnum.HOME} />;
      }

      if (!chosenProject && !restricted && !isInIFrame) {
        return <Redirect to={RouterUrlEnum.CHOOSE_PROJECT} />;
      }

      return <PropertyComponent {...globalConfig} {...detailedConfig} />;
    } else {
      if (isLoggedIn && restricted) {
        return <Redirect to={RouterUrlEnum.HOME} />;
      }

      return <PropertyComponent {...globalConfig} {...detailedConfig} />;
    }
  }

  render(): JSX.Element {
    return <Route>{this.getComponent()}</Route>;
  }
}

const mapStateToProps = (state: AppState): IStateProps => ({
  isLoggedIn: getIsLoggedInSelector(state),
  chosenProject: getChosenProjectSelector(state),
  isDirty: getIsDirtySelector(state),
});

export default connect(mapStateToProps)(ExtendedRoute);
